import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Table, useFeatures } from '@cordata/buffet';
import { uniq } from 'lodash';
import log from '~/utils/event/log';

const ARRAY_DELIMITER = ', ';
const DATE_FORMAT = 'MM/DD/YYYY';
const TIME_FORMAT = 'hh:mm A';

const ReportTable = ({
  data,
  title,
  columns,
  fixHeight,
  sortBy,
  height,
  groupByColumn,
  groupByDisplayColumns,
  pageSize,
}) => {
  const [defaultSorted, setDefaultSorted] = useState([]);
  const { hasAppIdentity } = useFeatures();

  useEffect(() => {
    if (sortBy) {
      setDefaultSorted(sortBy.map(s => ({
        id: s.columnName,
        desc: s.desc,
      })));
    }
  }, [sortBy]);

  const hasDisplayColumns =
    !!groupByColumn &&
    !!groupByDisplayColumns &&
    groupByDisplayColumns.length > 0;

  const displayColumnSet = hasDisplayColumns
    ? new Set(groupByDisplayColumns)
    : new Set();

  const visibleColumns = hasDisplayColumns
    ? columns.filter(c => !displayColumnSet.has(c.columnName))
    : columns;

  const tableColumns = visibleColumns?.map(c => ({
    accessor: c.columnName,
    Header: c.displayHeader,
    Cell: (v) => {
      if (v.value) {
        if (c.type === 'date') {
          return (
            <span>{format(v.value, DATE_FORMAT)}</span>
          );
        } else if (c.type === 'time') {
          return (
            <span>{format(v.value, TIME_FORMAT)}</span>
          );
        } else if (c.type === 'dateTime') {
          return (
            <span>{format(v.value, `${DATE_FORMAT} ${TIME_FORMAT}`)}</span>
          );
        } else if (c.type === 'boolean') {
          if (v.value === true || (typeof v.value === 'string' && v.value.match(/true|yes/i))) {
            return (
              <FontAwesomeIcon icon="check" />
            );
          }

          return null;
        } else if (c.type === 'array') {
          if (Array.isArray(v.value)) {
            return (
              <span>{v.value.join(ARRAY_DELIMITER)}</span>
            );
          }

          if (typeof v.value === 'string' && v.value.startsWith('[')) {
            return (
              <span>{v.value.slice(1, -1).split(',').map(x => x.trim()).join(ARRAY_DELIMITER)}</span>
            );
          }

          return (
            <span>{v.value}</span>
          );
        }

        return (
          <span>{v.value}</span>
        );
      }

      return <span />;
    },
    ...groupByColumn && {
      Aggregated: () => <></>,
      Expander: () => <></>,
    },
  }));

  if (hasDisplayColumns && tableColumns) {
    tableColumns.unshift({
      accessor: groupByColumn,
      Header: groupByDisplayColumns.map(colName => {
        const col = columns.find(c => c.columnName === colName);
        return col ? col.displayHeader : colName;
      }).join(', '),
      Cell: (v) => <span>{v.value}</span>,
      PivotValue: ({ row }) => {
        const values = groupByDisplayColumns
          .map(colName => row[colName] || '')
          .filter(Boolean);
        return <span>{values.join(', ')}</span>;
      },
      ...groupByColumn && {
        Aggregated: () => <></>,
        Expander: () => <></>,
      },
    });
  }

  return (<>
    {title && (
      <h2 className="m-3">{title}</h2>
    )}

    <Table
      defaultPageSize={pageSize}
      pageSizeOptions={uniq([10, 20, 50, 100, pageSize]).sort((a, b) => a - b)}
      getTbodyProps={() => {
        if (fixHeight && height) {
          return {
            style: {
              maxHeight: height,
              overflowY: 'auto',
            },
          };
        }

        return {};
      }}
      getTrProps={(state, rowInfo) => {
        // eslint-disable-next-line
        if (rowInfo.original?.patient_uid) {
          return {
            style: {
              cursor: 'pointer',
            },
            onClick: (e, handleOriginal) => {
              const goToPatient = (patientUid) => {
                // if identity, we want to log that the user viewed the patient. this was not happeneing before for non identity users, so not sure if they want it too.
                if (hasAppIdentity) {
                  log.create({ patientUid, action: 'ViewedPatient' });
                }
                window.open(`/patients/${rowInfo.original.patient_uid}`, '_blank');
              };
              return handleOriginal
                ? handleOriginal()
                : !rowInfo.original.isForeign
                  ? goToPatient(rowInfo.original.patient_uid)
                  : null;
            },
          };
        }

        return {};
      }}
      defaultSorted={defaultSorted}
      columns={tableColumns}
      data={data}
      {...groupByColumn && {
        pivotBy: [groupByColumn],
        expanded: Array(data.length).fill(true),
      }}
    />
  </>);
};

export default ReportTable;
